<template>
  <div class="on-form">
    <!-- Step 1: Entity Selector (AI chat-based) -->
    <v-card variant="outlined" class="mb-4 pa-3">
      <div class="text-subtitle-2 font-weight-bold mb-2">
        <v-icon size="18" class="mr-1">mdi-domain</v-icon>
        {{ tl('Select Entity') }}
      </div>
      <div class="entity-fields">
        <v-text-field
          :model-value="entityRegNo"
          :label="tl('Company Number (New)')"
          variant="outlined"
          density="compact"
          readonly
          :placeholder="tl('Click to select entity via AI')"
          style="cursor: pointer;"
          @click="handleEntityPickRequest"
        >
          <template #append-inner>
            <v-btn
              icon="mdi-creation"
              color="primary"
              size="x-small"
              variant="text"
              density="comfortable"
              @click.stop="handleEntityPickRequest"
            />
          </template>
        </v-text-field>
        <v-text-field
          v-if="selectedEntityCd"
          :model-value="entityRegNoOld || '-'"
          :label="tl('Company Number (Old)')"
          variant="outlined"
          density="compact"
          readonly
        />
        <v-text-field
          v-if="entityCompanyName"
          :model-value="entityCompanyName"
          :label="tl('Company Name')"
          variant="outlined"
          density="compact"
          readonly
          class="entity-name-full"
        />
      </div>
    </v-card>

    <!-- Step 2: Section Type Selector (Single-select) -->
    <v-card variant="outlined" class="mb-4 pa-4" :class="{ 'card-disabled': !selectedEntityCd }">
      <div class="text-subtitle-1 font-weight-bold mb-3">
        <v-icon class="mr-2">mdi-format-list-bulleted-type</v-icon>
        {{ tl('Notification Type') }}
      </div>
      <v-select
        v-model="selectedSectionType"
        :items="sectionTypeOptions"
        item-title="label"
        item-value="value"
        :label="tl('Section Type')"
        variant="outlined"
        density="compact"
        :readonly="readOnly"
        :disabled="!selectedEntityCd"
        :hint="!selectedEntityCd ? tl('Please select an entity first') : ''"
        persistent-hint
        clearable
      />
      <div v-if="selectedEntry" class="d-flex flex-wrap gap-1 mt-2">
        <v-chip
          color="info"
          variant="tonal"
          size="small"
        >
          {{ tl('Section') }} {{ selectedEntry.value }}: {{ selectedEntry.actionTitle }} — {{ selectedEntry.officerType }}
        </v-chip>
      </div>
    </v-card>

    <!-- Extension of Time (EOT) -->
    <v-card v-if="selectedEntityCd && selectedSectionType" variant="outlined" class="mb-4 pa-4">
      <div class="text-subtitle-1 font-weight-bold mb-3" style="color: rgb(var(--v-theme-on-surface));">
        {{ tl('Extension of Time (EOT)') }}
      </div>
      <v-divider class="mb-3" />
      <v-checkbox
        v-model="eotEnabled"
        :label="tl('ADD EXTENSION OF TIME')"
        density="compact"
        :readonly="readOnly"
        hide-details="auto"
        class="mb-2"
      />
      <template v-if="eotEnabled">
        <div class="eot-fields">
          <v-menu v-model="eotDateMenu" :close-on-content-click="false" location="bottom">
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                :model-value="formatDisplayDate(eotGrantedDate)"
                :label="tl('EOT Granted Date')"
                :placeholder="tl('DD MMM YYYY')"
                data-required
                :rules="[rules.required]"
                readonly
                variant="outlined"
                density="compact"
                hide-details="auto"
              >
                <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
              </v-text-field>
            </template>
            <v-date-picker
              :model-value="ymdToDate(eotGrantedDate)"
              color="primary"
              show-adjacent-months
              @update:model-value="(v: any) => { eotGrantedDate = dateToYmd(v); eotDateMenu = false; }"
            />
          </v-menu>
          <div class="eot-file-row">
            <v-file-input
              v-model="eotAttachment"
              :label="tl('EOT Attachment')"
              data-required
              variant="outlined"
              density="compact"
              :readonly="readOnly"
              :error-messages="eotFileError"
              accept=".pdf,.tiff,.tif"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              @update:model-value="validateEotFile"
            />
            <div class="text-caption text-medium-emphasis mt-1">{{ tl('Supported formats') }}: .TIFF, .PDF ({{ tl('max 2MB') }})</div>
          </div>
        </div>
      </template>
    </v-card>

    <!-- Section B/E/H: 신규 등록 폼 (appoint) -->
    <v-card v-if="selectedEntityCd && selectedSectionType && isAppointAction" variant="outlined" class="mb-4">
      <v-card-title class="d-flex align-center py-2 px-4 on-section-title">
        <v-icon size="20" class="mr-2" color="primary">mdi-account-plus</v-icon>
        <span class="text-subtitle-1 font-weight-bold" style="color: #1a2433; letter-spacing: 0.01em;">{{ selectedEntry?.label || tl('New Officer Appointment') }}</span>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4">
        <component
          :is="activeAppointForm"
          ref="appointFormRef"
          :read-only="readOnly"
          @update:form-data="handleAppointFormUpdate"
        />
      </v-card-text>
    </v-card>

    <!-- Section A/C/D/F/G/I: 기존 Officer 목록 (change/cease) -->
    <DirectorAccordion
      v-else-if="selectedEntityCd && selectedSectionType && !isAppointAction && directorList.isFetched.value"
      :directors="directorList.directors.value"
      :loading="directorList.isLoading.value"
      :error="directorList.errorMessage.value"
      :fetched="directorList.isFetched.value"
      :read-only="readOnly"
      :director-form-data-map="directorFormDataMap"
      :section-label="selectedEntry?.label || ''"
      :officer-type="selectedEntry?.officerType || 'DIRECTOR'"
      :section-action="selectedEntry?.action || ''"
      :edit-form-component="activeEditForm"
      @update:director-data="handleDirectorDataUpdate"
      @retry="directorList.fetchDirectors(selectedEntityCd, getPositionGroup(selectedEntry?.officerType || ''))"
    />
    <!-- Loading state for directors (change/cease only) -->
    <div v-else-if="selectedEntityCd && selectedSectionType && !isAppointAction && directorList.isLoading.value" class="d-flex align-center justify-center py-4">
      <v-progress-circular indeterminate color="primary" size="24" width="2" class="mr-2" />
      <span class="text-body-2 text-medium-emphasis">{{ tl('Loading officers...') }}</span>
    </div>

    <!-- Declaration (항상 표시) -->
    <v-card v-if="selectedEntityCd && selectedSectionType" variant="outlined" class="mb-4 pa-4">
      <div class="text-subtitle-1 font-weight-bold mb-3" style="color: rgb(var(--v-theme-on-surface));">
        {{ tl('Declaration') }}
      </div>
      <v-divider class="mb-3" />
      <v-checkbox
        v-model="declarationConfirmed"
        density="compact"
        :readonly="readOnly"
        hide-details="auto"
        class="mb-3"
      >
        <template #label>
          <span class="text-body-2 font-weight-medium" style="font-style: italic;">
            {{ tl('I CONFIRM THAT THE FACTS AND INFORMATION STATED IN THIS DOCUMENT ARE TRUE AND TO THE BEST OF MY KNOWLEDGE') }}
          </span>
        </template>
      </v-checkbox>
      <div class="declaration-attention">
        <div class="text-body-2 font-weight-bold mb-1">{{ tl('Attention') }}:</div>
        <div class="text-caption text-medium-emphasis" style="line-height: 1.5;">
          {{ tl('It is an offence under section 591 of the Companies Act 2016 to make or authorize the making of a statement that a person knows is false or misleading and that person may be liable, upon conviction, to imprisonment for a term not exceeding ten years or to a fine not exceeding RM3 million or to both.') }}
        </div>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, nextTick, provide } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import { useFormConfig } from './composables/useFormConfig';
import { useDirectorList } from './composables/useDirectorList';
import type { OfficerPositionGroup } from './composables/useDirectorList';
import DirectorAccordion from './DirectorAccordion.vue';
import DirectorAppointForm from './forms/DirectorAppointForm.vue';
import ManagerAppointForm from './forms/ManagerAppointForm.vue';
import SecretaryAppointForm from './forms/SecretaryAppointForm.vue';
import DirectorEditForm from './forms/DirectorEditForm.vue';
import ManagerEditForm from './forms/ManagerEditForm.vue';
import SecretaryEditForm from './forms/SecretaryEditForm.vue';
import type { DirectorFormData } from './forms/DirectorEditForm.vue';
import type { AppointFormData } from './forms/DirectorAppointForm.vue';

// Section별 폼 매핑
const appointFormMap: Record<string, any> = {
  DIRECTOR: DirectorAppointForm,
  MANAGER: ManagerAppointForm,
  SECRETARY: SecretaryAppointForm,
};
const editFormMap: Record<string, any> = {
  DIRECTOR: DirectorEditForm,
  MANAGER: ManagerEditForm,
  SECRETARY: SecretaryEditForm,
};

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

// Props
interface Props {
  taskConfig?: any;
  taskId?: string;
  entityCd?: string;
  loading?: boolean;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  taskId: undefined,
  entityCd: '',
  loading: false,
  readOnly: false,
});

const emit = defineEmits<{
  'update:formData': [data: any];
  'entity-pick-request': [];
  'directorship-entity-pick-request': [];
  'contact-pick-request': [entityCd: string];
}>();

// Form config (section type metadata)
const { enums, getActionTitle, getSectionType } = useFormConfig();

// ── Directorship entity pick (provide/inject for child forms) ──
let _pendingDirectorshipCallback: ((entity: any) => void) | null = null;

provide('pickDirectorshipEntity', (callback: (entity: any) => void) => {
  _pendingDirectorshipCallback = callback;
  emit('directorship-entity-pick-request');
});

function applyDirectorshipEntity(entity: Record<string, any> | null | undefined) {
  if (!entity || !_pendingDirectorshipCallback) return;
  _pendingDirectorshipCallback(entity);
  _pendingDirectorshipCallback = null;
}

// ── Contact pick (provide/inject for child forms) ──
let _pendingContactCallback: ((contact: any) => void) | null = null;

provide('pickContact', (callback: (contact: any) => void) => {
  _pendingContactCallback = callback;
  emit('contact-pick-request', selectedEntityCd.value);
});

function applyContact(contact: Record<string, any> | null | undefined) {
  if (!contact || !_pendingContactCallback) return;
  _pendingContactCallback(contact);
  _pendingContactCallback = null;
}

// Director list composable
const directorList = useDirectorList();

// Section type selection (single-select)
const selectedSectionType = ref<string>('');
const sectionTypeOptions = enums.sectionTypes;

interface SelectedEntry {
  value: string;
  action: string;
  officerType: string;
  label: string;
  actionTitle: string;
}

const selectedEntry = computed<SelectedEntry | null>(() => {
  if (!selectedSectionType.value) return null;
  const st = getSectionType(selectedSectionType.value);
  return {
    value: selectedSectionType.value,
    action: st?.action || '',
    officerType: st?.officerType || '',
    label: st?.label || selectedSectionType.value,
    actionTitle: getActionTitle(st?.action || ''),
  };
});

// Minimal form data (companyNo, preIdNumber)
const formData: Record<string, any> = reactive({});

// Company / Entity state
const entities = ref<any[]>([]);
const entitiesLoading = ref(false);
const selectedEntityCd = ref<string>('');
const entityRegNo = ref<string>('');
const entityRegNoOld = ref<string>('');
const entityCompanyName = ref<string>('');

// Entity companyNo for SSM API calls
let _entityCompanyNo = '';

// EOT state
const eotEnabled = ref(false);
const eotGrantedDate = ref('');
const eotAttachment = ref<File[] | null>(null);
const eotFileError = ref('');
const EOT_MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const EOT_ALLOWED_TYPES = ['.pdf', '.tiff', '.tif'];
const EOT_ALLOWED_MIMES = ['application/pdf', 'image/tiff'];
const eotDateMenu = ref(false);

function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return dateStr;
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${d} ${months[m - 1]} ${y}`;
}
function ymdToDate(ymd: string): Date | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function dateToYmd(d: Date | null): string {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const rules = {
  required: (v: any) => !!v || tl('This field is required'),
};

function validateEotFile(files: File[] | null) {
  eotFileError.value = '';
  if (!files || files.length === 0) return;
  const file = files[0];
  // 확장자 검사
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  if (!EOT_ALLOWED_TYPES.includes(ext)) {
    eotFileError.value = tl('Only .TIFF and .PDF files are allowed.');
    eotAttachment.value = null;
    return;
  }
  // MIME 타입 검사
  if (file.type && !EOT_ALLOWED_MIMES.includes(file.type)) {
    eotFileError.value = tl('Only .TIFF and .PDF files are allowed.');
    eotAttachment.value = null;
    return;
  }
  // 파일 크기 검사
  if (file.size > EOT_MAX_FILE_SIZE) {
    eotFileError.value = tl('File size must not exceed 2MB.');
    eotAttachment.value = null;
    return;
  }
}

// Declaration state
const declarationConfirmed = ref(false);

// Director form data map (officerIdx → DirectorFormData)
const directorFormDataMap = reactive<Record<string, DirectorFormData>>({});

// Appoint form data (Section B/E/H — 신규 등록)
const appointFormRef = ref<{ validate: () => boolean; getValidationErrors: () => string[]; loadSavedData?: (data: any) => void } | null>(null);
const appointFormData = ref<AppointFormData | null>(null);

// action 기반 분기
const isAppointAction = computed(() => selectedEntry.value?.action === 'appoint');

// Section별 동적 컴포넌트
const activeAppointForm = computed(() => appointFormMap[selectedEntry.value?.officerType || ''] || DirectorAppointForm);
const activeEditForm = computed(() => editFormMap[selectedEntry.value?.officerType || ''] || DirectorEditForm);

// Track director changes
const hasAnyDirectorChanges = computed(() => {
  if (isAppointAction.value) {
    return appointFormData.value?._isDirty || false;
  }
  return Object.values(directorFormDataMap).some(d => d._isDirty);
});

function handleDirectorDataUpdate(officerIdx: string, data: DirectorFormData) {
  directorFormDataMap[officerIdx] = data;
  emitFormData();
}

function handleAppointFormUpdate(data: AppointFormData) {
  appointFormData.value = data;
  emitFormData();
}

// ── Company options ──
async function loadCompanyOptions() {
  entitiesLoading.value = true;
  try {
    const response = await apiClient.get('/startTask/common/entities');
    const data = (response as any)?.data || response;
    const items = data?.entities || data?.companies || [];
    entities.value = (items || []).map((c: any) => {
      const regNo = (c.regNo || c.REG_NO || '').toString().trim();
      const regNoOld = (c.regNoOld || c.REG_NO_OLD || '').toString().trim();
      const companyName = (c.companyName || c.COMPANY_NAME || '').toString().trim();
      const entityCd = (c.entityCd || c.ENTITY_CD || '').toString().trim();
      return {
        regNo, regNoOld, companyName, entityCd,
        display: companyName ? `${regNoOld || regNo}(${companyName})` : (regNoOld || regNo),
      };
    }).filter((x: any) => (x.regNo || x.regNoOld) && x.entityCd);
  } catch {
    entities.value = [];
  } finally {
    entitiesLoading.value = false;
  }
}

// ── Entity pick (AI chat) ──
function handleEntityPickRequest() {
  if (!props.readOnly) {
    emit('entity-pick-request');
  }
}

/**
 * AI 챗봇에서 entity 선택 시 부모가 호출.
 * companyNo: regNoOld 우선 (SSM 구번호).
 */
function applyEntity(entity: Record<string, any> | null | undefined) {
  if (!entity) return;

  let regNoOld = (entity.regNoOld || entity.REG_NO_OLD || '').toString().trim();
  let regNo = (entity.regNo || entity.REG_NO || entity.registrationNo || '').toString().trim();
  let name = entity.companyName || entity.entityName || entity.COMPANY_NAME || '';
  const entityCd = entity.entityCd || entity.ENTITY_CD || '';

  // 챗봇 entity 에 regNoOld 가 없으면 로드된 entities 목록에서 보완
  if (entityCd || regNo) {
    const matched = entities.value.find(e =>
      (entityCd && e.entityCd === entityCd) || (regNo && (e.regNo === regNo || e.regNoOld === regNo))
    );
    if (matched) {
      if (!regNoOld) regNoOld = (matched.regNoOld || '').toString().trim();
      if (!regNo) regNo = (matched.regNo || '').toString().trim();
      if (!name) name = matched.companyName || '';
    }
  }

  // SSM 스크래핑은 OLD 번호 사용
  const companyNoForPayload = regNoOld || regNo;

  // Clear state
  selectedSectionType.value = '';
  Object.keys(formData).forEach(key => delete formData[key]);
  resetDirectorState();

  // Set entity
  selectedEntityCd.value = entityCd;
  entityRegNo.value = regNo;
  entityRegNoOld.value = regNoOld;
  entityCompanyName.value = name;
  _entityCompanyNo = companyNoForPayload;
  formData['companyInfo.companyNo'] = companyNoForPayload;

  console.log('[OfficerNotificationForm] applyEntity', { companyNoForPayload, regNo, regNoOld, name, entityCd });
  emitFormData();
}

function resetDirectorState() {
  directorList.reset();
  Object.keys(directorFormDataMap).forEach(key => delete directorFormDataMap[key]);
  appointFormData.value = null;
  eotEnabled.value = false;
  eotGrantedDate.value = '';
  eotAttachment.value = null;
  eotFileError.value = '';
  declarationConfirmed.value = false;
}

// officerType → positionGroup 매핑
function getPositionGroup(officerType: string): OfficerPositionGroup {
  const upper = (officerType || '').toUpperCase();
  if (upper === 'MANAGER') return 'manager';
  if (upper === 'SECRETARY') return 'secretary';
  return 'director';
}

// ── Section type watcher → 하단 초기화 + action에 따라 분기 ──
watch(selectedSectionType, (newVal, oldVal) => {
  // Section Type 변경 시 하단 데이터 항상 초기화
  Object.keys(directorFormDataMap).forEach(key => delete directorFormDataMap[key]);
  appointFormData.value = null;
  directorList.reset();
  eotEnabled.value = false;
  eotGrantedDate.value = '';
  eotAttachment.value = null;
  eotFileError.value = '';
  declarationConfirmed.value = false;

  if (newVal && selectedEntityCd.value) {
    const newAction = selectedEntry.value?.action || '';
    // appoint (Section B/E/H) → 신규 등록 폼, fetch 불필요
    // change/cease (Section A/C/D/F/G/I) → 기존 목록 fetch
    if (newAction !== 'appoint') {
      const newGroup = getPositionGroup(selectedEntry.value?.officerType || '');
      directorList.fetchDirectors(selectedEntityCd.value, newGroup);
    }
  }
  emitFormData();
});

// Entity 변경 시 상태 초기화 (Section Type 선택 시 fetch — appoint 제외)
watch(selectedEntityCd, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    resetDirectorState();
    if (selectedSectionType.value && selectedEntry.value?.action !== 'appoint') {
      const group = getPositionGroup(selectedEntry.value?.officerType || '');
      directorList.fetchDirectors(newVal, group);
    }
  } else if (!newVal) {
    resetDirectorState();
  }
});

onMounted(async () => {
  await loadCompanyOptions();

  if (props.entityCd) {
    const entity = entities.value.find(e => e.entityCd === props.entityCd);
    if (entity) {
      const regNoOld = (entity.regNoOld || '').toString().trim();
      const regNo = (entity.regNo || '').toString().trim();
      _entityCompanyNo = regNoOld || regNo;
      formData['companyInfo.companyNo'] = _entityCompanyNo;
      selectedEntityCd.value = entity.entityCd;
      entityRegNo.value = regNo;
      entityRegNoOld.value = regNoOld;
      entityCompanyName.value = entity.companyName || '';
      console.log('[OfficerNotificationForm] Auto-selected entity:', _entityCompanyNo, entity.companyName);
    }
  }
});

function emitFormData() {
  emit('update:formData', getFormData());
}

// ===== Exposed methods =====

function getFormData(): Record<string, any> {
  const action = selectedEntry.value?.action || '';

  // appoint → 신규 등록 데이터
  if (action === 'appoint') {
    return {
      _sectionType: selectedSectionType.value || '',
      _action: action,
      _officerType: selectedEntry.value?.officerType || '',
      _hasChanges: appointFormData.value?._isDirty || false,
      companyNo: formData['companyInfo.companyNo'] || '',
      companyEntityCd: selectedEntityCd.value || '',
      companyInfo: {
        companyNo: formData['companyInfo.companyNo'] || '',
        entityCd: selectedEntityCd.value || '',
      },
      appointData: appointFormData.value || null,
      eot: {
        enabled: eotEnabled.value,
        grantedDate: eotGrantedDate.value || '',
        attachment: eotAttachment.value || null,
      },
      declarationConfirmed: declarationConfirmed.value,
    };
  }

  // change/cease → 기존 officer 수정 데이터
  const directorChanges = Object.entries(directorFormDataMap)
    .filter(([_, d]) => d._isDirty)
    .map(([idx, d]) => ({ officerIdx: idx, ...d }));

  return {
    _sectionType: selectedSectionType.value || '',
    _action: action,
    _officerType: selectedEntry.value?.officerType || '',
    _hasChanges: hasAnyDirectorChanges.value,
    companyNo: formData['companyInfo.companyNo'] || '',
    preIdNumber: formData['companyInfo.preIdNumber'] || '',
    companyEntityCd: selectedEntityCd.value || '',
    companyInfo: {
      companyNo: formData['companyInfo.companyNo'] || '',
      preIdNumber: formData['companyInfo.preIdNumber'] || '',
      entityCd: selectedEntityCd.value || '',
    },
    directorChanges,
    eot: {
      enabled: eotEnabled.value,
      grantedDate: eotGrantedDate.value || '',
      attachment: eotAttachment.value || null,
    },
    declarationConfirmed: declarationConfirmed.value,
  };
}

async function validate(): Promise<boolean> {
  if (!selectedEntityCd.value) return false;
  if (!selectedSectionType.value) return false;
  if (!declarationConfirmed.value) return false;
  if (eotEnabled.value) {
    if (!eotGrantedDate.value) return false;
    if (!eotAttachment.value || eotAttachment.value.length === 0) return false;
  }
  // Validate child form (appoint action)
  if (isAppointAction.value && appointFormRef.value?.validate) {
    if (!appointFormRef.value.validate()) return false;
  }
  return true;
}

function getValidationErrors(): string[] {
  const errors: string[] = [];
  if (!selectedEntityCd.value) errors.push(tl('Please select an entity first'));
  if (!selectedSectionType.value) errors.push(tl('Please select a section type'));
  // Child form validation errors (appoint action)
  if (isAppointAction.value && appointFormRef.value?.getValidationErrors) {
    const childErrors = appointFormRef.value.getValidationErrors();
    if (childErrors.length > 0) {
      errors.push(tl('Personal Information / Address:'));
      childErrors.forEach(e => errors.push(`  - ${e}`));
    }
  }
  if (!declarationConfirmed.value) errors.push(tl('Please confirm the declaration'));
  if (eotEnabled.value && !eotGrantedDate.value) errors.push(tl('Please enter EOT Granted Date'));
  if (eotEnabled.value && (!eotAttachment.value || eotAttachment.value.length === 0)) errors.push(tl('Please attach EOT document'));
  return errors;
}

function resetForm() {
  selectedEntityCd.value = '';
  entityRegNo.value = '';
  entityRegNoOld.value = '';
  entityCompanyName.value = '';
  selectedSectionType.value = '';
  _entityCompanyNo = '';
  Object.keys(formData).forEach(key => delete formData[key]);
  resetDirectorState();
}

async function loadSavedData(data: any) {
  if (!data) return;

  // Restore entity
  if (data.companyEntityCd) selectedEntityCd.value = data.companyEntityCd;
  else if (props.entityCd) selectedEntityCd.value = props.entityCd;

  const savedCompanyInfo = data.companyInfo || {};
  if (savedCompanyInfo.companyNo) {
    _entityCompanyNo = savedCompanyInfo.companyNo;
    formData['companyInfo.companyNo'] = savedCompanyInfo.companyNo;
  }
  if (savedCompanyInfo.preIdNumber) {
    formData['companyInfo.preIdNumber'] = savedCompanyInfo.preIdNumber;
  }

  if (selectedEntityCd.value) {
    const entity = entities.value.find(e => e.entityCd === selectedEntityCd.value);
    if (entity) {
      const regNoOld = (entity.regNoOld || '').toString().trim();
      const regNo = (entity.regNo || '').toString().trim();
      entityRegNo.value = regNo;
      entityRegNoOld.value = regNoOld;
      entityCompanyName.value = entity.companyName || '';
      _entityCompanyNo = regNoOld || regNo;
    }
  }

  // Restore section type
  if (data._sectionType) {
    selectedSectionType.value = data._sectionType;
  }

  // Restore EOT
  if (data.eot) {
    eotEnabled.value = !!data.eot.enabled;
    eotGrantedDate.value = data.eot.grantedDate || '';
  }

  // Restore declaration
  if (data.declarationConfirmed !== undefined) {
    declarationConfirmed.value = !!data.declarationConfirmed;
  }

  // Restore appoint form data (wait for the child form to render)
  if (data._action === 'appoint' && data.appointData) {
    await nextTick();
    // Child form may need multiple ticks to mount
    let attempts = 0;
    const maxAttempts = 20;
    while (attempts < maxAttempts) {
      const ref = appointFormRef.value as any;
      if (ref?.loadSavedData) {
        ref.loadSavedData(data.appointData);
        break;
      }
      attempts++;
      await new Promise(r => setTimeout(r, 50));
    }
  }

  emitFormData();
}

defineExpose({
  validate,
  getFormData,
  resetForm,
  loadSavedData,
  getValidationErrors,
  applyEntity,
  applyDirectorshipEntity,
  applyContact,
});
</script>

<style scoped>
.on-form {
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.entity-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
  align-items: start;
}

.entity-name-full {
  grid-column: 1 / -1;
}

.card-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.eot-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  align-items: start;
  max-width: 600px;
}

.declaration-attention {
  background: rgba(var(--v-theme-warning), 0.05);
  border-left: 3px solid rgba(var(--v-theme-warning), 0.6);
  padding: 12px 16px;
  border-radius: 0 4px 4px 0;
}

.on-section-title {
  background: rgba(var(--v-theme-primary), 0.06);
}

/* ── Required field indicator (red asterisk) ── */
:deep([data-required] .v-label)::before {
  content: '* ';
  color: #f44336;
  font-weight: 700;
}
</style>
